import { OrderDetail } from '@interfaces/OrderTypes';
import OrderDetailsBarPrice from './OrderDetailsBarPrice';
import AccordionButton from '@components/interface/AccordionButton';
import OrderDetailsProduct from './OrderDetailsProduct';
import { formatToCurrency } from '@utils/formats';
import { Button } from '@plick/ui';
import { plickTheme } from '@theme/index';
import { useGetOrdersWithStatus } from '../hooks/orders';
import { IconFlameFilled, IconTrash } from '@tabler/icons-react';
import { useElectron } from '@utils/hooks/useElectron';
import { useReprintPrinters } from '@store/actions/electronPrintSettings';
import { useWebBlePrint } from '@utils/hooks/useWebBlePrint';
import { usePrintOrder } from '../hooks/usePrintOrder';
import { Icon } from '@iconify/react';

type SelfServiceOrdersModalProps = {
  orders: OrderDetail[];
  isAbleToDelete: boolean;
};

export const SelfServiceOrdersModal = ({ orders, isAbleToDelete }: SelfServiceOrdersModalProps) => {
  const { printOrder } = usePrintOrder();
  const { handleUpdateStatus, isUpdatingOrderStatus } = useGetOrdersWithStatus();
  const reprintPrinters = useReprintPrinters();
  const { isAutoPrinting, reprintOrder, isElectronInstance } = useElectron();
  const webBle = useWebBlePrint();

  const orderSubtotal = orders.reduce((acc, order) => {
    return acc + (order.pedido.subtotal || 0);
  }, 0);

  const orderDiscounts = orders.reduce((acc, order) => {
    return acc + (order.pedido.coupon_amount || 0);
  }, 0);

  const orderTotal = orders.reduce((acc, order) => {
    return acc + (order.pedido.total || 0);
  }, 0);

  return (
    <div className="flex flex-col justify-between">
      <div className="max-w-full w-[600px]">
        <ul className="px-2 overflow-y-auto max-h-[50vh]">
          {orders.map((order) => (
            <li key={order.pedido.id}>
              <AccordionButton
                title={`Folio ${order.pedido.folio}`}
                subtitle={`${order.productos.length} producto${order.productos.length > 1 ? 's' : ''}`}
                expanded={false}
                headerRightElements={
                  <div className="flex items-center gap-2 relative">
                    {!order?.pedido?.estatus || order.pedido.estatus === 'pending' ? (
                      <div>
                        <Button.Square
                          color="#feae33"
                          lightHoverColor="#feae33"
                          icon={<IconFlameFilled width={24} />}
                          disabled={isUpdatingOrderStatus}
                          text=""
                          onClick={() => handleUpdateStatus(order.pedido, 'next', true)}
                        />
                        <p className="absolute text-xs text-[#feae33] font-bold">Aceptar</p>
                      </div>
                    ) : null}
                    <div>
                      <Button.Square
                        color={plickTheme.colors.secondary}
                        lightHoverColor={plickTheme.colors.secondary}
                        icon={<Icon icon="ic:round-print" width={30} />}
                        text=""
                        onClick={() => {
                          const detailedOrder = {
                            pedido: {
                              ...order.pedido,
                              numero_mesa: order.pedido.self_service?.table_number,
                            },
                            productos: order.productos,
                          };
                          if (webBle.isWebBleActive) {
                            webBle.printOrder({ orderId: order.pedido.id, detailedOrder });
                            return;
                          }
                          printOrder({ orderId: order.pedido.id, detailedOrder });
                        }}
                        disabled={webBle.status.isPrinting}
                      />
                    </div>
                    {isElectronInstance ? (
                      <div>
                        <Button.Square
                          color={plickTheme.colors.secondary}
                          lightHoverColor={plickTheme.colors.secondary}
                          icon={<Icon icon="typcn:printer" width={30} />}
                          text=""
                          onClick={() =>
                            reprintOrder({
                              orderId: order.pedido.id,
                              detailedOrder: {
                                pedido: {
                                  ...order.pedido,
                                  numero_mesa: order.pedido.self_service?.table_number,
                                },
                                productos: order.productos,
                              },
                              printers: reprintPrinters,
                            })
                          }
                          disabled={isAutoPrinting}
                        />
                      </div>
                    ) : null}
                    {isAbleToDelete ? (
                      <div>
                        <Button.Square
                          color={plickTheme.colors.dangerDark}
                          lightHoverColor={plickTheme.colors.dangerDark}
                          icon={<IconTrash width={20} />}
                          disabled={isUpdatingOrderStatus}
                          text=""
                          onClick={() => {
                            handleUpdateStatus(order.pedido, 'cancel');
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                }
              >
                <div>
                  <div className="flex w-full text-color-white rounded-md bg-color-secondary justify-between items-center p-1 font-bold">
                    <p className="ml-2">Articulo</p>
                    <p className="mr-2">Costo</p>
                  </div>
                  {order.productos.map((product) => (
                    <div key={product.id}>
                      <OrderDetailsProduct key={product.id} {...product} />
                    </div>
                  ))}
                </div>
                {order.pedido.self_service?.waiter_name ? (
                  <div className="mt-3 bg-color-gray-blue-light rounded-md">
                    <p className="p-3">
                      <b>Atendió:</b> {order.pedido.self_service?.waiter_name}
                    </p>
                  </div>
                ) : null}
                {order.pedido.coupon_amount && (
                  <div className="mt-3 bg-color-gray-blue-light rounded-md">
                    <p className="p-3">
                      <b>Descuento:</b> {formatToCurrency(order.pedido.coupon_amount)} | Código:{' '}
                      {order.pedido.coupon_code}
                    </p>
                  </div>
                )}
                {order.pedido.comentario && (
                  <div className="mt-3 bg-color-gray-blue-light rounded-md">
                    <p className="p-3">
                      <b>Comentarios:</b> {order.pedido.comentario}
                    </p>
                  </div>
                )}
              </AccordionButton>
            </li>
          ))}
        </ul>
      </div>
      <div className="bottom-0 sticky bg-color-white">
        <OrderDetailsBarPrice
          textOne="Subtotal"
          bgColor="color-gray_light"
          txtColor="color-black"
          price={orderSubtotal}
        />
        {orderDiscounts ? (
          <OrderDetailsBarPrice
            textOne="Descuento"
            bgColor="color-white"
            txtColor="color-black"
            price={orderDiscounts}
          />
        ) : null}
        <OrderDetailsBarPrice textOne="Total" bgColor="color-secondary" txtColor="color-white" price={orderTotal} />
      </div>
    </div>
  );
};
